// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import isEmpty from 'lodash/isEmpty';
import ModuleX from '@tcp/core/src/components/common/molecules/ModuleX';
import PropTypes from 'prop-types';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import styles, { StyledModuleWrapper, StyledModule } from '../styles/GridPromo.style';
import withStyles from '../../../hoc/withStyles';

const ModuleDynamic = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "moduleDynamic" */ '@tcp/core/src/components/common/molecules/ModuleDynamic'
  )
);

const ModuleContainer = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "moduleContainer" */ '@tcp/core/src/components/common/molecules/ModuleContainer'
  )
);

const ModuleTabbedCarousel = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "moduleTabbedCarousel" */ '@tcp/core/src/components/common/molecules/ModuleTabbedCarousel'
  )
);

const styliticsProvider = 'stylitics_carousel';

const getSplitText = (val) => {
  return (val && val[0] && val[0].text) || '';
};

export const extractAspectRatios = (aspectRatioStr) => {
  const aspectRatios = {
    aspectRatioD: null,
    aspectRatioT: null,
    aspectRatioM: null,
  };

  const ratioPairs = aspectRatioStr?.split('|');

  ratioPairs?.forEach((pair) => {
    const [key, value] = pair.split('-');
    switch (key) {
      case 'D':
        aspectRatios.aspectRatioD = parseInt(value, 10);
        break;
      case 'T':
        aspectRatios.aspectRatioT = parseInt(value, 10);
        break;
      case 'M':
        aspectRatios.aspectRatioM = parseInt(value, 10);
        break;
      default:
        break;
    }
  });

  return aspectRatios;
};

export const isAspectRatioAllowed = ({
  aspectRatio,
  isSingleTile,
  isDoubleTile,
  isShopByLookMView,
}) => {
  const shouldApplyAspectRatio =
    (!isSingleTile && !isDoubleTile) || (isDoubleTile && isShopByLookMView);

  return shouldApplyAspectRatio ? extractAspectRatios(aspectRatio) : {};
};

const getIsHorizontalVariation = (variation, headLineParts) =>
  variation === 'horizontal' && !isEmpty(headLineParts);

const getIsModuleName = (moduleName) => {
  const defaultModulesName = {
    isModuleTabbedCarousel: false,
    isModuleX: false,
    isModuleContainer: false,
    isModuleDynamic: false,
  };
  if (moduleName === 'moduleTabbedCarousel') {
    return {
      ...defaultModulesName,
      isModuleTabbedCarousel: true,
    };
  }
  if (moduleName === 'moduleDynamic') {
    return {
      ...defaultModulesName,
      isModuleDynamic: true,
    };
  }
  if (moduleName === 'moduleX') {
    return {
      ...defaultModulesName,
      isModuleX: true,
    };
  }
  if (moduleName === 'module_container') {
    return {
      ...defaultModulesName,
      isModuleContainer: true,
    };
  }
  return defaultModulesName;
};

const getIsDefaultBlock = (variation, headLineParts, textItems, descriptionParts, promoWrapper) =>
  variation !== 'vertical' &&
  !isEmpty(headLineParts) &&
  !isEmpty(textItems) &&
  !isEmpty(descriptionParts) &&
  !isEmpty(promoWrapper);

const renderHorizontal = (className, headLineParts) => (
  <div className={`${className} horizontal-promo`}>
    {headLineParts &&
      headLineParts.map((line, index) => {
        return (
          <BodyCopy
            color="black"
            fontFamily="secondary"
            fontSize="fs24"
            textAlign="center"
            key={`HeadLineParts_${index.toString()}`}
          >
            {line}
          </BodyCopy>
        );
      })}
  </div>
);

const renderDefaultBLock = (
  className,
  headLineParts,
  textItems,
  descriptionParts,
  promoWrapper
) => (
  <div className={`${className} product-tile promo-div`}>
    <div className="headline-wrapper">
      {headLineParts &&
        headLineParts.map((line, index) => {
          return (
            <React.Fragment key={`HeadLineWrapper_${index.toString()}`}>
              <BodyCopy
                Component="span"
                className={`${className} highlighted-text`}
                color="black"
                fontFamily="secondary"
                fontSize={['fs28', 'fs32', 'fs36']}
                fontWeight="black"
                textAlign="center"
              >
                {line}
              </BodyCopy>
              <br />
            </React.Fragment>
          );
        })}
    </div>
    <div className="middle-text-wrapper">
      <BodyCopy
        color="white"
        fontFamily="secondary"
        fontSize={['fs18', 'fs20', 'fs24']}
        fontWeight="black"
        textAlign="center"
      >
        {textItems && textItems[1] && textItems[1].text}
      </BodyCopy>
      <BodyCopy
        color="white"
        fontFamily="secondary"
        fontSize={['fs18', 'fs20', 'fs24']}
        fontWeight="black"
        textAlign="center"
      >
        {textItems && textItems[2] && textItems[2].text}
      </BodyCopy>
    </div>
    <div className="description-wrapper">
      {descriptionParts &&
        descriptionParts.map((description, index) => {
          return (
            <BodyCopy
              color="white"
              fontFamily="secondary"
              fontSize={['fs10', 'fs12', 'fs12']}
              textAlign="center"
              key={`description_Wrapper_${index.toString()}`}
            >
              {description}
            </BodyCopy>
          );
        })}
    </div>
    <div className="cta-wrapper">
      {promoWrapper &&
        promoWrapper.map((cta, index) => {
          if (cta.type === 'button') {
            return (
              <Button
                buttonVariation="variable-width"
                fill="BLUE"
                key={`button_promo_cta_wrapper_${index.toString()}`}
              >
                {cta.text}
              </Button>
            );
          }
          return (
            <Anchor
              to={cta.url}
              asPath={cta.url}
              target={cta.target}
              title={cta.title}
              anchorVariation="white"
              key={`anchor_cta_wrapper_${index.toString()}`}
            >
              {cta.text}
            </Anchor>
          );
        })}
    </div>
  </div>
);

const renderModuleDynamic = (
  hasMounted,
  promoObj,
  {
    aspectRatio,
    isSingleTile,
    isDoubleTile,
    isInGridPromo,
    pageCategoryId,
    isFullWidthTile,
    tileHeightValue,
    isShopByLookMView,
    isShopByLookTView,
    tileBadgeHeightValue,
    isShopByLookModuleConfig,
    processedBreadCrumbs,
    shouldAllowMaxHeight,
  }
) => {
  if (isShopByLookModuleConfig) {
    const aspectStyles = isAspectRatioAllowed({
      aspectRatio,
      isSingleTile,
      isDoubleTile,
      isShopByLookMView,
    });
    return (
      <StyledModuleWrapper aspectRatioObj={aspectStyles}>
        <StyledModule aspectRatioObj={aspectStyles}>
          <ModuleDynamic
            {...promoObj}
            isInGridPromo={isInGridPromo}
            aspectRatioObj={aspectStyles}
            isFullRowView={isFullWidthTile}
            pageCategoryId={pageCategoryId}
            isSingleTileView={isSingleTile}
            isDoubleTileView={isDoubleTile}
            tileHeightValue={tileHeightValue}
            isShopByLookTView={isShopByLookTView}
            isShopByLookMView={isShopByLookMView}
            tileBadgeHeightValue={tileBadgeHeightValue}
            processedBreadCrumbs={processedBreadCrumbs}
            shouldAllowMaxHeight={shouldAllowMaxHeight}
          />
        </StyledModule>
      </StyledModuleWrapper>
    );
  }
  return hasMounted ? <ModuleDynamic {...promoObj} /> : null;
};

const GridPromo = (props) => {
  const {
    className,
    promoObj = {},
    variation,
    isSingleTile,
    isDoubleTile,
    isInGridPromo,
    pageCategoryId,
    isFullWidthTile,
    tileHeightValue,
    tileBadgeHeightValue,
    processedBreadCrumbs,
    shouldAllowMaxHeight,
  } = props;
  const [isShopByLookTView, setIsShopByLookTView] = useState(false);
  const [isShopByLookMView, setIsShopByLookMView] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsShopByLookMView(window.innerWidth < 768);
      setIsShopByLookTView(window.innerWidth < 1200);
    };
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { textItems, subHeadLine, promoWrapper, moduleName, aspectRatio, provider } = promoObj;
  const headingLine = getSplitText(textItems);
  const headLineParts = headingLine.split('|');
  const descriptionLine = getSplitText(subHeadLine);
  const descriptionParts = descriptionLine.split('|');
  const hasMounted = useHasMounted();
  const isHorizontalVariation = getIsHorizontalVariation(variation, headLineParts);
  const { isModuleTabbedCarousel, isModuleX, isModuleContainer, isModuleDynamic } =
    getIsModuleName(moduleName);
  const isDefaultBlock = getIsDefaultBlock(
    variation,
    headLineParts,
    textItems,
    descriptionParts,
    promoWrapper
  );
  const isShopByLookModuleConfig = provider === styliticsProvider;

  if (isModuleX) {
    return hasMounted ? <ModuleX data={promoObj} {...promoObj} /> : null;
  }

  if (isModuleDynamic) {
    return renderModuleDynamic(hasMounted, promoObj, {
      aspectRatio,
      isSingleTile,
      isDoubleTile,
      isInGridPromo,
      pageCategoryId,
      isFullWidthTile,
      tileHeightValue,
      isShopByLookMView,
      isShopByLookTView,
      tileBadgeHeightValue,
      isShopByLookModuleConfig,
      processedBreadCrumbs,
      shouldAllowMaxHeight,
    });
  }

  if (isModuleContainer) {
    return hasMounted ? (
      <ModuleContainer
        {...promoObj}
        isInGridPromo={isInGridPromo}
        tileHeightValue={tileHeightValue}
        isDoubleTile={isDoubleTile}
        isSingleTile={isSingleTile}
        isFullWidthTile={isFullWidthTile}
      />
    ) : null;
  }

  if (isModuleTabbedCarousel) {
    return hasMounted ? <ModuleTabbedCarousel {...promoObj} /> : null;
  }

  if (isHorizontalVariation) {
    return renderHorizontal(className, headLineParts);
  }

  if (isDefaultBlock) {
    return renderDefaultBLock(className, headLineParts, textItems, descriptionParts, promoWrapper);
  }
  return null;
};

GridPromo.propTypes = {
  className: PropTypes.string,
  promoObj: PropTypes.shape({
    textItems: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      })
    ).isRequired,
    subHeadLine: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      })
    ),
    promoWrapper: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      })
    ),
    mediaWrapper: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      })
    ),
  }).isRequired,
  variation: PropTypes.string,
  isInGridPromo: PropTypes.string,
  pageCategoryId: PropTypes.string,
  isDoubleTile: PropTypes.bool,
  isSingleTile: PropTypes.bool,
  isFullWidthTile: PropTypes.bool,
  shouldAllowMaxHeight: PropTypes.bool,
  tileHeightValue: PropTypes.string.isRequired,
  tileBadgeHeightValue: PropTypes.string.isRequired,
  processedBreadCrumbs: PropTypes.shape({}).isRequired,
};

GridPromo.defaultProps = {
  className: '',
  variation: 'vertical',
  isInGridPromo: false,
  pageCategoryId: '',
  isSingleTile: false,
  isDoubleTile: false,
  isFullWidthTile: false,
  shouldAllowMaxHeight: false,
};

export default withStyles(GridPromo, styles);
export { GridPromo as GridPromoVanilla };
